import React, { useEffect, useMemo, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Info } from "lucide-react";
import { toast } from "@/hooks/use-toast";
import { api } from "@/api";
import { formatDateEU, formatError, computeDegreasingPrice } from "@/components/shared/helpers";

const TYPE_LABELS = {
  degreasing: "Dégraissage",
  laundry_full: "Blanchisserie complète",
  laundry_punctual: "Blanchisserie ponctuelle",
};

const STATUS_LABELS = {
  pending: { label: "En attente", tone: "bg-amber-500/20 text-amber-200 border-amber-500/40" },
  confirmed: { label: "Confirmée", tone: "bg-emerald-500/20 text-emerald-200 border-emerald-500/40" },
  rejected: { label: "Refusée", tone: "bg-slate-500/20 text-slate-200 border-slate-500/40" },
};

export default function AdminLaundryRequests({ properties = [] }) {
  const [rows, setRows] = useState([]);
  const [pricing, setPricing] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("all");
  const [propertyFilter, setPropertyFilter] = useState("all");
  const [detail, setDetail] = useState(null);
  const [confirmedDate, setConfirmedDate] = useState("");
  const [adminNote, setAdminNote] = useState("");
  const [finalPrice, setFinalPrice] = useState("");
  const [autoPriceHint, setAutoPriceHint] = useState("");
  const [saving, setSaving] = useState(false);
  const [sendEmail, setSendEmail] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      if (statusFilter !== "all") params.status = statusFilter;
      if (propertyFilter !== "all") params.property_id = propertyFilter;
      const [res, pricingRes] = await Promise.all([
        api.get("/admin/laundry-requests", { params }),
        api.get("/admin/laundry-pricing").catch(() => ({ data: [] })),
      ]);
      setRows(res.data || []);
      setPricing(pricingRes.data || []);
    } catch (e) {
      toast({ title: "Erreur", description: formatError(e), variant: "destructive" });
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [statusFilter, propertyFilter]);

  const openDetail = (row) => {
    setDetail(row);
    setConfirmedDate(row.period_start || "");
    setAdminNote("");
    setSendEmail(false);
    setAutoPriceHint("");
    // Prix pré-calculé — recalculé DYNAMIQUEMENT depuis la grille tarifaire actuelle
    // (pas depuis le snapshot dans req.articles). Si l'admin a modifié un prix dans
    // Paramètres, il se répercute ici immédiatement.
    if (row.request_type === "laundry_full") {
      const total = (row.articles || []).reduce((s, a) => {
        const currentArticle = pricing.find((p) => p.id === a.article_id);
        const unitPrice = currentArticle ? Number(currentArticle.price_ttc) : Number(a.unit_price_ttc);
        return s + Number(a.qty) * unitPrice;
      }, 0);
      setFinalPrice(total.toFixed(2));
    } else if (row.request_type === "laundry_punctual" && row.punctual_price_ttc != null) {
      setFinalPrice(String(row.punctual_price_ttc));
    } else if (row.request_type === "degreasing") {
      // Auto : lire degreasing_price_ttc du logement, sinon fallback cleaning × 1.6
      const prop = properties.find((p) => p.id === row.property_id);
      const { price, source } = computeDegreasingPrice(prop);
      setFinalPrice("");
      if (source === "configured") {
        setAutoPriceHint(`Prix "Auto" = ${price.toFixed(2)}€ (degreasing_price_ttc du logement)`);
      } else if (source === "estimated") {
        setAutoPriceHint(
          `⚠️ Tarif non configuré sur ce logement — prix estimé Auto = ${price.toFixed(2)}€ (prix ménage × 1,6). À confirmer manuellement.`
        );
      } else {
        setAutoPriceHint(
          "⚠️ Tarif non configuré et aucun prix ménage disponible — Tarif à confirmer par notre équipe. Saisissez un prix manuellement avant de confirmer."
        );
      }
    } else {
      setFinalPrice("");
    }
  };

  const decide = async (decision) => {
    if (!detail) return;
    setSaving(true);
    try {
      const payload = { decision, admin_note: adminNote || null, send_email_to_owner: sendEmail };
      if (decision === "confirmed") {
        if (detail.request_type !== "laundry_punctual" && !confirmedDate) {
          toast({ title: "Date requise", description: "Sélectionnez une date de confirmation.", variant: "destructive" });
          setSaving(false); return;
        }
        payload.confirmed_date = confirmedDate || null;
        if (finalPrice !== "") payload.final_price_ttc = Number(finalPrice);
      }
      const res = await api.post(`/admin/laundry-requests/${detail.id}/decide`, payload);
      toast({
        title: decision === "confirmed" ? "Demande confirmée" : "Demande refusée",
        description: res.data?.email_sent ? "Email envoyé au propriétaire." : "Enregistrée sans email.",
      });
      setDetail(null);
      await load();
    } catch (e) {
      toast({ title: "Erreur", description: formatError(e), variant: "destructive" });
    } finally { setSaving(false); }
  };

  const filteredRows = useMemo(() => rows, [rows]);

  return (
    <Card data-testid="admin-laundry-requests" className="border-white/10 bg-white/[0.08]">
      <CardHeader>
        <CardTitle data-testid="admin-laundry-requests-title" className="text-slate-100">Dégraissage / Blanchisserie</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex flex-wrap gap-3">
          <div className="min-w-[180px]">
            <Label className="text-xs text-slate-300">Statut</Label>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger data-testid="admin-laundry-filter-status" className="border-white/10 bg-white/[0.06]"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tous</SelectItem>
                <SelectItem value="pending">En attente</SelectItem>
                <SelectItem value="confirmed">Confirmées</SelectItem>
                <SelectItem value="rejected">Refusées</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="min-w-[220px]">
            <Label className="text-xs text-slate-300">Logement</Label>
            <Select value={propertyFilter} onValueChange={setPropertyFilter}>
              <SelectTrigger data-testid="admin-laundry-filter-property" className="border-white/10 bg-white/[0.06]"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tous</SelectItem>
                {properties.map((p) => <SelectItem key={p.id} value={p.id}>{p.short_name || p.residence_name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        </div>

        {loading ? (
          <div className="text-slate-400 text-sm">Chargement…</div>
        ) : filteredRows.length === 0 ? (
          <div data-testid="admin-laundry-empty" className="text-slate-400 text-sm">Aucune demande.</div>
        ) : (
          <div className="space-y-2">
            {filteredRows.map((r) => {
              const st = STATUS_LABELS[r.status] || STATUS_LABELS.pending;
              return (
                <div
                  key={r.id}
                  data-testid={`admin-laundry-row-${r.id}`}
                  onClick={() => openDetail(r)}
                  className="cursor-pointer rounded-md border border-white/10 bg-white/[0.03] p-3 hover:bg-white/[0.06] transition"
                >
                  <div className="flex items-start justify-between gap-3 flex-wrap">
                    <div className="min-w-0 flex-1">
                      <div className="text-sm font-medium text-slate-100 flex items-center gap-2 flex-wrap">
                        {r.property_name || r.property_id}
                        <Badge className={`${st.tone} border`}>{st.label}</Badge>
                        {!r.read_by_admin && r.status === "pending" && (
                          <span data-testid={`admin-laundry-row-${r.id}-unread`} className="inline-flex h-2 w-2 rounded-full bg-red-500" />
                        )}
                      </div>
                      <div className="text-xs text-slate-400 mt-0.5">
                        {TYPE_LABELS[r.request_type]}
                        {r.period_start && r.period_end && ` — période ${formatDateEU(r.period_start)} → ${formatDateEU(r.period_end)}`}
                        {r.confirmed_date && ` — retenu ${formatDateEU(r.confirmed_date)}`}
                      </div>
                      <div className="text-xs text-slate-500">{r.owner_name} • {new Date(r.created_at).toLocaleString("fr-FR")}</div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </CardContent>

      {/* ── Dialog de gestion ─────────────────────────────────────────── */}
      <Dialog open={!!detail} onOpenChange={(o) => { if (!o) setDetail(null); }}>
        <DialogContent data-testid="admin-laundry-detail-dialog" className="border-white/10 bg-[#0f1320] text-slate-100 sm:max-w-[600px]">
          <DialogHeader>
            <DialogTitle data-testid="admin-laundry-detail-title" className="text-slate-100">
              {detail && TYPE_LABELS[detail.request_type]} — {detail?.property_name}
            </DialogTitle>
          </DialogHeader>

          {detail && (
            <div className="space-y-3 text-sm">
              <div className="rounded-md border border-white/10 bg-white/[0.03] p-3 space-y-1">
                <div><b>Propriétaire :</b> {detail.owner_name} <span className="text-slate-400">({detail.owner_email})</span></div>
                {detail.period_start && detail.period_end && (
                  <div><b>Période souhaitée :</b> {formatDateEU(detail.period_start)} → {formatDateEU(detail.period_end)}</div>
                )}
                {detail.request_type === "laundry_punctual" && detail.punctual_description && (
                  <div><b>Description :</b> {detail.punctual_description}</div>
                )}
                {detail.request_type === "laundry_full" && detail.articles?.length > 0 && (
                  <div>
                    <b>Articles :</b>
                    <ul className="mt-1 ml-4 text-xs list-disc space-y-0.5">
                      {detail.articles.map((a, i) => (
                        <li key={i}>{a.name} × {a.qty} @ {Number(a.unit_price_ttc).toFixed(2)}€ = <b>{(a.qty * a.unit_price_ttc).toFixed(2)}€</b></li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>

              {detail.status === "pending" ? (
                <>
                  {detail.request_type !== "laundry_punctual" && (
                    <div>
                      <Label className="text-xs text-slate-300">Date de confirmation retenue (dans la période)</Label>
                      <Input
                        data-testid="admin-laundry-detail-confirmed-date"
                        type="date"
                        min={detail.period_start || undefined}
                        max={detail.period_end || undefined}
                        value={confirmedDate}
                        onChange={(e) => setConfirmedDate(e.target.value)}
                        className="border-white/10 bg-white/[0.06]"
                      />
                      {confirmedDate && (
                        <div data-testid="admin-laundry-detail-confirmed-date-fr" className="text-xs text-slate-400 mt-1">
                          Format français : <b className="text-slate-200">{formatDateEU(confirmedDate)}</b>
                        </div>
                      )}
                    </div>
                  )}
                  <div>
                    <div className="flex items-center gap-2">
                      <Label className="text-xs text-slate-300">Prix final TTC (optionnel — override le calcul)</Label>
                      <Popover>
                        <PopoverTrigger asChild>
                          <button
                            data-testid="admin-laundry-detail-pricing-info"
                            type="button"
                            className="inline-flex items-center gap-1 text-violet-300 hover:text-violet-200"
                            aria-label={detail.request_type === "degreasing" ? "Voir le tarif dégraissage du logement" : "Voir la grille tarifaire"}
                          >
                            <Info className="h-3.5 w-3.5" />
                          </button>
                        </PopoverTrigger>
                        <PopoverContent
                          data-testid="admin-laundry-detail-pricing-popover"
                          align="start"
                          side="bottom"
                          sideOffset={6}
                          collisionPadding={16}
                          className="w-[380px] border-white/10 bg-[#0f1320] text-slate-100 p-0 text-xs"
                        >
                          <div className="max-h-[min(60vh,420px)] overflow-y-auto p-3">
                            {detail.request_type === "degreasing" ? (
                              (() => {
                                const prop = properties.find((p) => p.id === detail.property_id);
                                const { price, source, cleaningBase } = computeDegreasingPrice(prop);
                                return (
                                  <div data-testid="admin-laundry-detail-pricing-degreasing">
                                    <div className="text-slate-300 mb-2">Tarif dégraissage (lecture seule)</div>
                                    <div className="rounded-md border border-white/10 bg-white/[0.03] p-2">
                                      {source === "configured" && (
                                        <div className="flex justify-between gap-3">
                                          <span className="text-slate-300">Tarif dégraissage de ce logement</span>
                                          <span data-testid="admin-laundry-detail-pricing-degreasing-value" className="text-slate-100 font-medium">
                                            {price.toFixed(2)}€
                                          </span>
                                        </div>
                                      )}
                                      {source === "estimated" && (
                                        <>
                                          <div className="flex justify-between gap-3">
                                            <span className="text-slate-300">Tarif estimé (prix ménage × 1,6)</span>
                                            <span data-testid="admin-laundry-detail-pricing-degreasing-value" className="text-slate-100 font-medium">
                                              {price.toFixed(2)}€
                                            </span>
                                          </div>
                                          <div className="mt-1 text-[11px] text-slate-500">
                                            Base ménage : {cleaningBase.toFixed(2)}€
                                          </div>
                                          <div data-testid="admin-laundry-detail-pricing-degreasing-warning" className="mt-2 rounded border border-amber-500/40 bg-amber-500/10 p-1.5 text-amber-100">
                                            ⚠️ Tarif non configuré sur ce logement — prix estimé, à confirmer.
                                          </div>
                                        </>
                                      )}
                                      {source === "none" && (
                                        <div data-testid="admin-laundry-detail-pricing-degreasing-tbd" className="rounded border border-amber-500/40 bg-amber-500/10 p-1.5 text-amber-100">
                                          Tarif à confirmer par notre équipe.
                                        </div>
                                      )}
                                    </div>
                                    <div className="mt-2 pt-2 border-t border-white/10 text-slate-400">
                                      Source : Logement → Tarification (<code>degreasing_price_ttc</code>). Le dégraissage n&apos;utilise pas la grille articles.
                                    </div>
                                  </div>
                                );
                              })()
                            ) : (
                              <>
                                <div className="text-slate-300 mb-2">Grille tarifaire (lecture seule)</div>
                                {["blanchisserie", "ameublement"].map((cat) => {
                                  const items = pricing.filter((a) => a.category === cat);
                                  if (!items.length) return null;
                                  return (
                                    <div key={cat} className="mb-3">
                                      <div className="font-medium text-slate-200 mb-1 sticky top-0 bg-[#0f1320] pb-1">{cat === "blanchisserie" ? "Blanchisserie" : "Ameublement"}</div>
                                      <ul className="space-y-0.5">
                                        {items.map((a) => (
                                          <li key={a.id} className="flex justify-between gap-3">
                                            <span className="text-slate-300">{a.name}</span>
                                            <span className="text-slate-100 font-medium">{Number(a.price_ttc).toFixed(2)}€</span>
                                          </li>
                                        ))}
                                      </ul>
                                    </div>
                                  );
                                })}
                                <div className="mt-2 pt-2 border-t border-white/10 text-slate-400">
                                  Source unique : Paramètres → Blanchisserie. Modifier un prix ici met à jour tous les calculs Auto.
                                </div>
                              </>
                            )}
                          </div>
                        </PopoverContent>
                      </Popover>
                    </div>
                    <Input
                      data-testid="admin-laundry-detail-final-price"
                      type="number" step="0.10" min="0"
                      value={finalPrice}
                      onChange={(e) => setFinalPrice(e.target.value)}
                      placeholder="Auto"
                      className="border-white/10 bg-white/[0.06]"
                    />
                    {autoPriceHint && (
                      <div data-testid="admin-laundry-detail-auto-hint" className="text-xs text-amber-200 mt-1">
                        {autoPriceHint}
                      </div>
                    )}
                  </div>
                  <div>
                    <Label className="text-xs text-slate-300">Note admin (optionnelle)</Label>
                    <Textarea
                      data-testid="admin-laundry-detail-note"
                      value={adminNote}
                      onChange={(e) => setAdminNote(e.target.value)}
                      className="border-white/10 bg-white/[0.06] min-h-[60px]"
                    />
                  </div>
                  <label className="flex items-center gap-2 text-xs text-slate-300">
                    <input
                      data-testid="admin-laundry-detail-send-email"
                      type="checkbox"
                      checked={sendEmail}
                      onChange={(e) => setSendEmail(e.target.checked)}
                    />
                    Envoyer un email au propriétaire (bilingue FR/EN)
                  </label>
                </>
              ) : (
                <div className="rounded-md border border-white/10 bg-white/[0.03] p-3">
                  <div><b>Statut :</b> {STATUS_LABELS[detail.status]?.label}</div>
                  {detail.confirmed_date && <div><b>Date retenue :</b> {formatDateEU(detail.confirmed_date)}</div>}
                  {detail.final_price_ttc != null && <div><b>Prix final :</b> {Number(detail.final_price_ttc).toFixed(2)}€</div>}
                  {detail.admin_note && <div><b>Note :</b> {detail.admin_note}</div>}
                </div>
              )}
            </div>
          )}

          <DialogFooter className="gap-2">
            <Button data-testid="admin-laundry-detail-close" variant="outline" className="rounded-full border-white/10 bg-white/[0.04]" onClick={() => setDetail(null)}>Fermer</Button>
            {detail?.status === "pending" && (
              <>
                <Button data-testid="admin-laundry-detail-reject" variant="outline" className="rounded-full border-rose-500/40 bg-rose-500/10 text-rose-200 hover:bg-rose-500/20" disabled={saving} onClick={() => decide("rejected")}>
                  Refuser
                </Button>
                <Button data-testid="admin-laundry-detail-confirm" className="rounded-full bg-emerald-500 text-white hover:bg-emerald-400" disabled={saving} onClick={() => decide("confirmed")}>
                  {saving ? "…" : "Confirmer cette date"}
                </Button>
              </>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}
